import { sanitizeHtml } from '../../lib/sanitizeHtml';
import React, { useState, useEffect, useRef } from 'react';
import {
  Save,
  X,
  Image,
  Link,
  Facebook,
  Linkedin,
  Twitter,
  Instagram,
  Youtube,
  Globe,
  AlertTriangle,
  Eye,
  Loader,
  CheckCircle,
  XCircle,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  Type,
  Palette,
  Upload,
  FolderOpen,
  Folder,
  File,
  ChevronRight,
  Home
} from 'lucide-react';
import SignatureDocumentPicker from './SignatureDocumentPicker';
import { useTranslation } from 'react-i18next';

const SOCIAL_PLATFORMS = [
  { id: 'facebook', label: 'Facebook', icon: Facebook, color: '#1877F2' },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
  { id: 'twitter', label: 'X (Twitter)', icon: Twitter, color: '#000000' },
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: '#E4405F' },
  { id: 'youtube', label: 'YouTube', icon: Youtube, color: '#FF0000' }
];

const EmailSignatureEditor = ({ 
  backendUrl, 
  authToken, 
  emailAccount,
  existingSignature,
  onSave,
  onCancel 
}) => {
  const { t } = useTranslation('admin');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  
  // Sélecteur de documents
  const [showDocumentPicker, setShowDocumentPicker] = useState(false);
  const [cabinetFolders, setCabinetFolders] = useState([]);
  const [cabinetDocuments, setCabinetDocuments] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderBreadcrumb, setFolderBreadcrumb] = useState([{ id: null, name: t('emailSignature.rootFolder') }]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  
  const editorRef = useRef(null);
  
  // État du formulaire
  const [form, setForm] = useState({
    content_html: '',
    logo_url: '',
    logo_enabled: false,
    website_url: '',
    website_enabled: false,
    social_links: SOCIAL_PLATFORMS.map(p => ({
      platform: p.id,
      url: '',
      enabled: false
    })),
    disclaimer_text: '',
    disclaimer_enabled: false
  });

  // Initialiser l'éditeur au montage
  useEffect(() => {
    if (editorRef.current && form.content_html) {
      editorRef.current.innerHTML = form.content_html;
    }
  }, []);

  useEffect(() => {
    if (existingSignature) {
      // Charger la signature existante
      const socialLinks = SOCIAL_PLATFORMS.map(p => {
        const existing = existingSignature.social_links?.find(s => s.platform === p.id);
        return {
          platform: p.id,
          url: existing?.url || '',
          enabled: existing?.enabled || false
        };
      });
      
      setForm({
        content_html: existingSignature.content_html || '',
        logo_url: existingSignature.logo_url || '',
        logo_enabled: existingSignature.logo_enabled || false,
        website_url: existingSignature.website_url || '',
        website_enabled: existingSignature.website_enabled || false,
        social_links: socialLinks,
        disclaimer_text: existingSignature.disclaimer_text || '',
        disclaimer_enabled: existingSignature.disclaimer_enabled || false
      });
      
      // Mettre à jour l'éditeur après un court délai pour s'assurer que le DOM est prêt
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = existingSignature.content_html || '';
        }
      }, 0);
    }
  }, [existingSignature]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  const showError = (msg) => {
    setErrorMessage(msg);
    setTimeout(() => setErrorMessage(''), 5000);
  };

  // Fonctions pour le sélecteur de documents du cabinet
  const loadCabinetContents = async (folderId = null) => {
    setLoadingDocs(true);
    try {
      // Charger les dossiers
      const foldersRes = await fetch(
        `${backendUrl}/api/admin/cabinet/folders${folderId ? `?parent_id=${folderId}` : ''}`,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      const foldersData = await foldersRes.json();
      setCabinetFolders(Array.isArray(foldersData) ? foldersData : (foldersData.folders || []));

      // Charger les documents (filtrer images uniquement)
      const docsRes = await fetch(
        `${backendUrl}/api/admin/cabinet/documents${folderId ? `?folder_id=${folderId}` : ''}`,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      const docsData = await docsRes.json();
      const allDocs = Array.isArray(docsData) ? docsData : (docsData.documents || []);
      // Filtrer pour n'afficher que les images
      const imageDocs = allDocs.filter(doc => 
        doc.mime_type?.startsWith('image/') || 
        /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(doc.original_filename || doc.filename)
      );
      setCabinetDocuments(imageDocs);
    } catch (error) {
      console.error('Erreur chargement documents cabinet:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  const openDocumentPicker = () => {
    setShowDocumentPicker(true);
    setCurrentFolderId(null);
    setFolderBreadcrumb([{ id: null, name: t('emailSignature.rootFolder') }]);
    loadCabinetContents(null);
  };

  const navigateToFolder = async (folder) => {
    setCurrentFolderId(folder.id);
    setFolderBreadcrumb(prev => [...prev, { id: folder.id, name: folder.name }]);
    await loadCabinetContents(folder.id);
  };

  const navigateToBreadcrumb = async (index) => {
    const target = folderBreadcrumb[index];
    setCurrentFolderId(target.id);
    setFolderBreadcrumb(prev => prev.slice(0, index + 1));
    await loadCabinetContents(target.id);
  };

  const selectDocument = async (doc) => {
    // Récupérer l'image et la convertir en base64 pour l'inliner dans la signature
    // (évite la dépendance à un token JWT qui expire)
    try {
      const previewUrl = `${backendUrl}/api/admin/cabinet/documents/${doc.id}/preview?token=${authToken}`;
      const res = await fetch(previewUrl, { headers: { 'Authorization': `Bearer ${authToken}` } });
      if (res.ok) {
        const blob = await res.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          setForm(prev => ({ ...prev, logo_url: reader.result }));
        };
        reader.readAsDataURL(blob);
      } else {
        // Fallback : URL avec token (au moins le preview fonctionnera côté admin)
        setForm(prev => ({ ...prev, logo_url: previewUrl }));
      }
    } catch (e) {
      const previewUrl = `${backendUrl}/api/admin/cabinet/documents/${doc.id}/preview?token=${authToken}`;
      setForm(prev => ({ ...prev, logo_url: previewUrl }));
    }
    setShowDocumentPicker(false);
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleEditorChange = () => {
    if (editorRef.current) {
      setForm(prev => ({
        ...prev,
        content_html: editorRef.current.innerHTML
      }));
    }
  };

  const updateSocialLink = (platform, field, value) => {
    setForm(prev => ({
      ...prev,
      social_links: prev.social_links.map(link =>
        link.platform === platform
          ? { ...link, [field]: value }
          : link
      )
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        email_account_id: emailAccount.id,
        content_html: form.content_html,
        logo_url: form.logo_url || null,
        logo_enabled: form.logo_enabled,
        website_url: form.website_url || null,
        website_enabled: form.website_enabled,
        social_links: form.social_links.filter(link => link.enabled && link.url),
        disclaimer_text: form.disclaimer_text,
        disclaimer_enabled: form.disclaimer_enabled
      };

      const url = existingSignature
        ? `${backendUrl}/api/admin/mail/signatures/${existingSignature.id}`
        : `${backendUrl}/api/admin/mail/signatures`;
      
      const method = existingSignature ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(t('emailSignature.saved'));
        if (onSave) onSave(data);
      } else {
        const error = await response.json();
        showError(error.detail || t('emailSignature.saveError'));
      }
    } catch (error) {
      showError(t('emailSignature.connError'));
    } finally {
      setSaving(false);
    }
  };

  const generatePreviewHTML = () => {
    let html = '';
    
    // Contenu principal
    if (form.content_html) {
      html += `<div style="margin-bottom: 15px;">${form.content_html}</div>`;
    }
    
    // Logo
    if (form.logo_enabled && form.logo_url) {
      html += `<div style="margin-bottom: 15px;"><img src="${form.logo_url}" alt="Logo" style="max-height: 60px; max-width: 200px;" /></div>`;
    }
    
    // Réseaux sociaux
    const activeSocials = form.social_links.filter(s => s.enabled && s.url);
    if (activeSocials.length > 0) {
      html += '<div style="margin-bottom: 15px;">';
      activeSocials.forEach(social => {
        const platform = SOCIAL_PLATFORMS.find(p => p.id === social.platform);
        html += `<a href="${social.url}" style="margin-right: 10px; color: ${platform?.color || '#333'}; text-decoration: none;">${platform?.label || social.platform}</a>`;
      });
      html += '</div>';
    }
    
    // Site web
    if (form.website_enabled && form.website_url) {
      html += `<div style="margin-bottom: 15px;"><a href="${form.website_url}" style="color: #4F46E5;">🌐 ${form.website_url}</a></div>`;
    }
    
    // Disclaimer
    if (form.disclaimer_enabled && form.disclaimer_text) {
      html += `<div style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #6b7280;">${form.disclaimer_text.replace(/\n/g, '<br/>')}</div>`;
    }
    
    return html;
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-blue-50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-slate-900">{t('emailSignature.editorTitle')}</h3>
            <p className="text-sm text-slate-600 mt-1">
              {t('emailSignature.account')} <span className="font-medium">{emailAccount?.email_address}</span>
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="mx-6 mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center">
          <CheckCircle className="text-emerald-600 mr-2" size={18} />
          <span className="text-emerald-700 text-sm">{successMessage}</span>
        </div>
      )}
      {errorMessage && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-xl p-3 flex items-center">
          <XCircle className="text-red-600 mr-2" size={18} />
          <span className="text-red-700 text-sm">{errorMessage}</span>
        </div>
      )}

      <div className="p-6 space-y-6">
        {/* Section 1: Contenu principal avec éditeur riche */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">
            <Type size={16} className="inline mr-2" />
            {t('emailSignature.textLabel')}
          </label>
          
          {/* Barre d'outils */}
          <div className="flex items-center space-x-1 p-2 bg-slate-50 rounded-t-xl border border-b-0 border-slate-200">
            <button
              type="button"
              onClick={() => execCommand('bold')}
              className="p-2 hover:bg-slate-200 rounded transition-colors"
              title={t('emailSignature.boldTitle')}
            >
              <Bold size={16} />
            </button>
            <button
              type="button"
              onClick={() => execCommand('italic')}
              className="p-2 hover:bg-slate-200 rounded transition-colors"
              title={t('emailSignature.italicTitle')}
            >
              <Italic size={16} />
            </button>
            <button
              type="button"
              onClick={() => execCommand('underline')}
              className="p-2 hover:bg-slate-200 rounded transition-colors"
              title={t('emailSignature.underlineTitle')}
            >
              <Underline size={16} />
            </button>
            <div className="w-px h-6 bg-slate-300 mx-2" />
            <button
              type="button"
              onClick={() => execCommand('justifyLeft')}
              className="p-2 hover:bg-slate-200 rounded transition-colors"
              title={t('emailSignature.alignLeftTitle')}
            >
              <AlignLeft size={16} />
            </button>
            <button
              type="button"
              onClick={() => execCommand('justifyCenter')}
              className="p-2 hover:bg-slate-200 rounded transition-colors"
              title={t('emailSignature.alignCenterTitle')}
            >
              <AlignCenter size={16} />
            </button>
            <div className="w-px h-6 bg-slate-300 mx-2" />
            <input
              type="color"
              onChange={(e) => execCommand('foreColor', e.target.value)}
              className="w-8 h-8 cursor-pointer rounded"
              title={t('emailSignature.textColorTitle')}
            />
          </div>
          
          {/* Éditeur */}
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning={true}
            onInput={handleEditorChange}
            className="min-h-[120px] p-4 border border-slate-200 rounded-b-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            style={{ whiteSpace: 'pre-wrap', direction: 'ltr', unicodeBidi: 'normal', textAlign: 'left' }}
          />
          <p className="text-xs text-slate-500 mt-1">
            {t('emailSignature.textHint')}
          </p>
        </div>

        {/* Section 2: Logo */}
        <div className="p-4 bg-slate-50 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <label className="flex items-center text-sm font-semibold text-slate-700">
              <Image size={16} className="mr-2" />
              {t('emailSignature.logoLabel')}
            </label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={form.logo_enabled}
                onChange={(e) => setForm(prev => ({ ...prev, logo_enabled: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:ring-2 peer-focus:ring-indigo-500 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
            </label>
          </div>
          {form.logo_enabled && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  type="url"
                  value={form.logo_url}
                  onChange={(e) => setForm(prev => ({ ...prev, logo_url: e.target.value }))}
                  placeholder={t('emailSignature.logoPlaceholder')}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  type="button"
                  onClick={openDocumentPicker}
                  className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors flex items-center space-x-2"
                >
                  <FolderOpen size={16} />
                  <span>{t('emailSignature.browse')}</span>
                </button>
              </div>
              {form.logo_url && (
                <div className="flex items-center space-x-3 p-2 bg-white rounded-lg border border-slate-200">
                  <img 
                    src={form.logo_url} 
                    alt={t('emailSignature.logoPreviewAlt')} 
                    className="h-12 max-w-32 object-contain"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  <span className="text-sm text-slate-500 truncate flex-1">{form.logo_url}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Section 3: Réseaux sociaux */}
        <div className="p-4 bg-slate-50 rounded-xl">
          <label className="block text-sm font-semibold text-slate-700 mb-3">
            <Globe size={16} className="inline mr-2" />
            {t('emailSignature.socialLabel')}
          </label>
          <div className="space-y-3">
            {SOCIAL_PLATFORMS.map(platform => {
              const socialLink = form.social_links.find(s => s.platform === platform.id);
              const IconComponent = platform.icon;
              
              return (
                <div key={platform.id} className="flex items-center space-x-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={socialLink?.enabled || false}
                      onChange={(e) => updateSocialLink(platform.id, 'enabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-slate-200 peer-focus:ring-2 peer-focus:ring-indigo-500 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                  <div className="flex items-center w-28">
                    <IconComponent size={18} style={{ color: platform.color }} className="mr-2" />
                    <span className="text-sm text-slate-700">{platform.label}</span>
                  </div>
                  {socialLink?.enabled && (
                    <input
                      type="url"
                      value={socialLink?.url || ''}
                      onChange={(e) => updateSocialLink(platform.id, 'url', e.target.value)}
                      placeholder={t('emailSignature.socialUrlPlaceholder', { platform: platform.label })}
                      className="flex-1 px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Section 4: Lien site web */}
        <div className="p-4 bg-slate-50 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <label className="flex items-center text-sm font-semibold text-slate-700">
              <Link size={16} className="mr-2" />
              {t('emailSignature.websiteLabel')}
            </label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={form.website_enabled}
                onChange={(e) => setForm(prev => ({ ...prev, website_enabled: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:ring-2 peer-focus:ring-indigo-500 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
            </label>
          </div>
          {form.website_enabled && (
            <input
              type="url"
              value={form.website_url}
              onChange={(e) => setForm(prev => ({ ...prev, website_url: e.target.value }))}
              placeholder={t('emailSignature.websitePlaceholder')}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          )}
        </div>

        {/* Section 5: Lignes de prévention / Disclaimer */}
        <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
          <div className="flex items-center justify-between mb-3">
            <label className="flex items-center text-sm font-semibold text-amber-800">
              <AlertTriangle size={16} className="mr-2" />
              {t('emailSignature.disclaimerLabel')}
            </label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={form.disclaimer_enabled}
                onChange={(e) => setForm(prev => ({ ...prev, disclaimer_enabled: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-amber-200 peer-focus:ring-2 peer-focus:ring-amber-500 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
            </label>
          </div>
          {form.disclaimer_enabled && (
            <textarea
              value={form.disclaimer_text}
              onChange={(e) => setForm(prev => ({ ...prev, disclaimer_text: e.target.value }))}
              placeholder={t('emailSignature.disclaimerPlaceholder')}
              rows={4}
              className="w-full px-4 py-2 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white"
            />
          )}
        </div>

        {/* Prévisualisation */}
        <div className="border-t border-slate-200 pt-6">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800"
          >
            <Eye size={16} className="mr-2" />
            {showPreview ? t('emailSignature.previewHide') : t('emailSignature.previewShow')}
          </button>
          
          {showPreview && (
            <div className="mt-4 p-4 bg-white border border-slate-200 rounded-xl">
              <p className="text-xs text-slate-500 mb-3">{t('emailSignature.previewHint')}</p>
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(generatePreviewHTML()) }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-6 border-t border-slate-200 bg-slate-50 flex items-center justify-end space-x-3">
        <button
          onClick={onCancel}
          className="px-6 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors"
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
        >
          {saving ? <Loader className="animate-spin" size={16} /> : <Save size={16} />}
          <span>{saving ? t('emailSignature.saving') : t('emailSignature.save')}</span>
        </button>
      </div>

      {/* Modal de sélection de documents du cabinet */}
      <SignatureDocumentPicker
        showDocumentPicker={showDocumentPicker}
        setShowDocumentPicker={setShowDocumentPicker}
        folderBreadcrumb={folderBreadcrumb}
        navigateToBreadcrumb={navigateToBreadcrumb}
        navigateToFolder={navigateToFolder}
        selectDocument={selectDocument}
        loadingDocs={loadingDocs}
        cabinetFolders={cabinetFolders}
        cabinetDocuments={cabinetDocuments}
        backendUrl={backendUrl}
        authToken={authToken}
      />
    </div>
  );
};

export default EmailSignatureEditor;
